import { DatePicker, LocalizationProvider, PickersDay } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { colors, TextField } from '@mui/material';
import Text from '../Text';
import { COLORS, fontSizes } from '../../constants/constants';
import { cn } from '../../utils/utils';
import dynamicfunction from '../../utils/dynamicfunction';
import ErrorText from './ErrorText';
import { useSelector } from 'react-redux';
import { getRejectedFieldData, getSectionRejectedFields, isRequiredEmpty } from '../../utils/functions';
import { ReactSVG } from 'react-svg';
import icons from '../../constants/icons';
import ViewReverse from '../ViewReverse';

const theme = createTheme({
  components: {
    MuiPickersYear: {
      styleOverrides: {
        root: {
          border: '1px solid #ccc',
          borderRadius: '4px',
          margin: '4px',
          height: '40px',
          cursor: 'pointer',
          flex: 1,
          maxWidth: '96px',
        },
      },
      selected: {
        flex: 1,
      },
    },
  },
});

const CustomDatePicker = ({
  label,
  divClasses,
  className,
  onChange,
  labelColor = 'text-primary-light',
  placeholder,
  selectedDate,
  isRequired = false,
  maxDate,
  minDate,
  isDisabled,
  labelDivClass,
  isLabelHeight = false,
  error,
  remarksMetadata = null,
  props,
}) => {
  const { isArabic } = dynamicfunction();
  const { rejection_summary, is_rejected, field_codes } = useSelector((state) => state.corporate);

  const rejectedFields = getSectionRejectedFields(rejection_summary, remarksMetadata?.sectionId);
  const rejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, remarksMetadata?.key);

  const isFieldDisabled = isDisabled || (is_rejected && remarksMetadata && (!rejectedField?.remarks || rejectedField?.remarks?.length < 1));


  return (
    <ThemeProvider theme={theme}>
      <div className={cn('w-full', divClasses)}>
        {label && (
          <div data-required-empty={isRequiredEmpty(isRequired, label, selectedDate)} className={cn(isLabelHeight ? 'md:h-9' : 'h-6', labelDivClass)}>
            <Text text={label} size={fontSizes.h14} color={labelColor} className={'mb-1 leading-4'} isRequired={isRequired} />
          </div>
        )}
        <div className={cn('border border-grey rounded py-[3px] px-2', className,
          isFieldDisabled && 'bg-disabled cursor-not-allowed',
          rejectedField?.remarks?.length > 0 && 'border-red-600'
        )}>
          <LocalizationProvider adapterLocale={isArabic ? 'ar' : 'en'} dateAdapter={AdapterDayjs}>
            <DatePicker
              {...props}
              sx={{ height: '40px' }}
              maxDate={maxDate}
              minDate={minDate}
              disabled={isFieldDisabled}
              className={cn('w-full', isFieldDisabled && '  cursor-not-allowed')}
              onChange={(date) => {
                onChange(date);
              }}
              format={'DD/MM/YYYY'}
              value={selectedDate ? dayjs(selectedDate) : null}
              slots={{
                textField: TextField,
                day: (props) => (
                  <PickersDay
                    {...props}
                    sx={{
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      margin: ' 3px',
                      padding: '0px',
                      '&.Mui-selected': {
                        backgroundColor: COLORS.primary_light,
                        color: COLORS.white,
                        borderColor: COLORS.primary_light,
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: COLORS.primary_light + '80',
                        color: COLORS.white,
                      },
                      '&.Mui-selected:focus': {
                        backgroundColor: COLORS.primary_light,
                        color: COLORS.white,
                      },
                      '&.MuiPickersDay-today': {
                        borderColor: COLORS.primary_light + '30',
                        borderWidth: '2px',
                        backgroundColor: COLORS.primary_light + '30',
                        color: COLORS.black,
                      },
                      '&.Mui:hover': {
                        borderColor: COLORS.primary_light + '30',
                        borderWidth: '2px',
                        backgroundColor: COLORS.primary_light + '30',
                        color: COLORS.black,
                      },
                    }}
                  />
                ),
              }}
              enableAccessibleFieldDOMStructure={false}
              slotProps={{
                textField: {
                  variant: 'standard',
                  InputProps: {
                    disableUnderline: true,
                  },
                  inputProps: {
                    placeholder: placeholder || 'dd/mm/yyyy',
                  },
                  sx: {
                    '& .MuiInputBase-root': {
                      border: 'none',
                      color: rejectedField?.remarks?.length > 0 ? COLORS.red : COLORS.black,
                    },
                    '& .MuiInputBase-input': {
                      padding: '5px 0px',
                      fontSize: '14px',
                    },
                    '& .MuiInputBase-root.Mui-disabled, & .MuiInputBase-input.Mui-disabled': {
                      color: rejectedField?.remarks?.length > 0 ? COLORS.red : COLORS.black,
                      WebkitTextFillColor: rejectedField?.remarks?.length > 0 ? COLORS.red : COLORS.black,
                    },
                    '& .MuiInputBase-input.Mui-disabled::placeholder': {
                      WebkitTextFillColor: COLORS.dark_grey + '80',
                    },
                  },
                  dir: isArabic ? 'rtl' : 'ltr',
                },
                leftArrowIcon: {
                  fontSize: 'medium',
                  sx: { border: '1px solid #ccc', borderRadius: '50px', height: '30px', width: '30px', padding: '5px' },
                },
                rightArrowIcon: {
                  fontSize: 'medium',
                  sx: { border: '1px solid #ccc', borderRadius: '50px', height: '30px', width: '30px', padding: '5px' },
                },
              }}
            />
          </LocalizationProvider>
        </div>
        {error?.length > 0 && <ErrorText text={error} />}
        {
          rejectedField?.remarks?.length > 0 && (
            <ViewReverse className='items-center gap-2 mt-2'>
            <ReactSVG src={icons.field_info_outline} />
            <ErrorText text={rejectedField?.remarks} className={'mt-0'} />
          </ViewReverse>
          )
        }
      </div>
    </ThemeProvider>
  );
};

export default CustomDatePicker;
